import { create } from 'zustand';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { SimulationCueSheetRow } from '@/types/player/simulation-cue-sheet.types';
import {
  buildSimulationSelectedItemIds,
  findSimulationRowIndex,
  isSimulationRowPlayable,
  resolveInitialSimulationPointer,
  resolveSimulationPlaybackComplete,
  resolveSimulationStepMedia,
  resolveSimulationSegmentFromRow,
  simulationScopeIdleSelection,
  resolveSimulationSegmentPlayPointer,
  resolveSimulationAdvancePastSegment,
  resolveScopeIdlePlayItemId,
  shouldIgnoreSimulationPlaybackComplete,
  segmentKindToAnchor,
  isFlowControlInBetweenSegmentKind,
  type FlowControlSegmentKind,
  type SimulationCuePlaylistContext,
  type SimulationSegmentAnchor,
} from '@/helpers/simulation-cue-engine.helper';
import {
  clearSimulationPlayedReturnPreviousFromIndex,
  findSimulationPointerByRowKey,
  isSimulationReturnPreviousRow,
  isSimulationRowInAsideRunOfMain,
  markSimulationPlayedReturnPrevious,
  resolveSimulationMainPointer,
  simulationRowKey,
} from '@/helpers/simulation-return-previous.helper';
import { resolveFlowControlSimulationFlow } from '@/helpers/simulation-flow.helper';
import { resolveFlowControlExpandIdFromSegment } from '@/helpers/flow-control-playing-segment.helper';
import { resolveNextShowModeFromPreselect } from '@/helpers/media-playlist-next-show-mode.helper';
import { useFlowControlMediaExpandStore } from '@/state/stores/use-flow-control-media-expand-store';
import { useSimulationLocalCameraStore } from '@/state/stores/use-simulation-local-camera-store';

type ShowMode = 'mediaOnly' | 'both' | 'speakerOnly';
export type SimulationCuePlayMode = 'jump' | 'insert';

type FlowControlSimulationCueState = {
  planFlowId: string | null;
  revision: string | null;
  /** Merged PLAN flow used for playlist advance enrichment (session playlists from event store). */
  simulationFlow: FlowDto | null;
  rows: SimulationCueSheetRow[];
  /** Auto-cue / resume pointer (unchanged during Play once insert). */
  pointer: number;
  /**
   * Play-once insert: On air row while set. `pointer` stays as resume/Next target.
   * Null when no insert override is active.
   */
  overridePointer: number | null;
  /** Row key of the main cue item to resume when leaving a `return_previous` aside. */
  returnTargetRowKey: string | null;
  /** Session one-shot: consumed `return_previous` row keys for the current main cue. */
  playedReturnPreviousKeys: string[];
  /** On Air / playout show mode (immediate). */
  onAirShowMode: ShowMode;
  /** Next look-ahead show mode; copied to onAir on cue advance. */
  nextShowMode: ShowMode;
  /**
   * When set to the current Next cue identity, skip automatic nextShowMode preselect apply
   * (operator used the NEXT overlay). Cleared when Next identity changes.
   */
  heldNextShowModeCueIdentity: string | null;
  selectedItemIds: string[];
  activeSegmentKind: FlowControlSegmentKind | null;
  activeSegmentAnchor: SimulationSegmentAnchor | null;
  activeSegmentSlotId?: string;
  slideIndexByItemId: Record<string, number>;
  totalPagesByItemId: Record<string, number>;
  /** Wall-clock ms when the current pointer item last went on-air (playback-complete guard). */
  onAirStartedAtMs: number | null;
  hydrateCueSheet: (
    planFlowId: string,
    rows: SimulationCueSheetRow[],
    revision: string,
    simulationFlow?: FlowDto,
  ) => void;
  reset: () => void;
  setOnAirShowMode: (showMode: ShowMode) => void;
  setNextShowMode: (showMode: ShowMode) => void;
  holdNextShowModePreselect: (cueIdentity: string) => void;
  clearNextShowModePreselectHold: () => void;
  stepMedia: (delta: number, flowFallback?: FlowDto) => void;
  playMedia: (
    itemId: string,
    playlistContext?: SimulationCuePlaylistContext,
    cuePlayMode?: SimulationCuePlayMode,
  ) => void;
  stopMedia: (itemId: string, playlistContext?: SimulationCuePlaylistContext) => void;
  playSegment: (segmentKind: FlowControlSegmentKind, slotId?: string) => void;
  stopSegment: (segmentKind: FlowControlSegmentKind, slotId?: string) => void;
  stepSlide: (itemId: string, delta: number) => void;
  setSlideIndex: (itemId: string, page: number) => void;
  reportSlidePageCount: (itemId: string, totalPages: number) => void;
  toggleItem: (itemId: string) => void;
  handlePlaybackComplete: (itemId: string) => void;
};

const initialState = {
  planFlowId: null as string | null,
  revision: null as string | null,
  simulationFlow: null as FlowDto | null,
  rows: [] as SimulationCueSheetRow[],
  pointer: 0,
  overridePointer: null as number | null,
  returnTargetRowKey: null as string | null,
  playedReturnPreviousKeys: [] as string[],
  onAirShowMode: 'both' as ShowMode,
  nextShowMode: 'both' as ShowMode,
  heldNextShowModeCueIdentity: null as string | null,
  selectedItemIds: [] as string[],
  activeSegmentKind: null as FlowControlSegmentKind | null,
  activeSegmentAnchor: null as SimulationSegmentAnchor | null,
  activeSegmentSlotId: undefined as string | undefined,
  slideIndexByItemId: {} as Record<string, number>,
  totalPagesByItemId: {} as Record<string, number>,
  onAirStartedAtMs: null as number | null,
};

function touchSimulationOnAirClock(): number {
  return Date.now();
}

/** Effective On air cue index (insert override or auto pointer). */
export function resolveSimulationOnAirPointer(
  state: Pick<FlowControlSimulationCueState, 'pointer' | 'overridePointer'>,
): number {
  return state.overridePointer ?? state.pointer;
}

function resolveSimulationFlowForActions(
  state: Pick<FlowControlSimulationCueState, 'simulationFlow'>,
  flowFallback?: FlowDto,
): FlowDto | undefined {
  return state.simulationFlow ?? resolveFlowControlSimulationFlow(flowFallback);
}

function syncPointerSelection(
  state: Pick<FlowControlSimulationCueState, 'rows' | 'selectedItemIds'>,
  pointer: number,
): Partial<FlowControlSimulationCueState> {
  return {
    pointer,
    selectedItemIds: buildSimulationSelectedItemIds(state.rows, pointer),
  };
}

function syncSegmentFromRow(
  row: SimulationCueSheetRow | undefined,
): Pick<FlowControlSimulationCueState, 'activeSegmentKind' | 'activeSegmentAnchor' | 'activeSegmentSlotId'> {
  const segment = resolveSimulationSegmentFromRow(row);
  return {
    activeSegmentKind: (segment?.segmentKind as FlowControlSegmentKind) ?? null,
    activeSegmentAnchor: segment?.anchor ?? null,
    activeSegmentSlotId: segment?.slotId,
  };
}

function preserveSpeakerIds(selectedItemIds: string[]): string[] {
  return selectedItemIds.filter((id) => id.startsWith('remoteSpeaker:') || id.startsWith('roomCam:'));
}

function resumeFromOverride(state: FlowControlSimulationCueState): Partial<FlowControlSimulationCueState> {
  const resumeRow = state.rows[state.pointer];
  return {
    overridePointer: null,
    returnTargetRowKey: null,
    selectedItemIds: buildSimulationSelectedItemIds(
      state.rows,
      state.pointer,
      preserveSpeakerIds(state.selectedItemIds),
    ),
    ...syncSegmentFromRow(resumeRow),
    onAirStartedAtMs: touchSimulationOnAirClock(),
  };
}

function leaveReturnPreviousAside(
  state: FlowControlSimulationCueState,
  flow?: FlowDto,
): Partial<FlowControlSimulationCueState> {
  const asidePointer = state.overridePointer;
  const asideRow = asidePointer != null ? state.rows[asidePointer] : undefined;
  const returnPointer =
    findSimulationPointerByRowKey(state.rows, state.returnTargetRowKey) ??
    resolveSimulationMainPointer(state.rows, state.pointer, flow) ??
    state.pointer;
  const mainIndex = resolveSimulationMainPointer(state.rows, returnPointer, flow) ?? returnPointer;
  let played = state.playedReturnPreviousKeys;
  if (
    asidePointer != null &&
    asideRow &&
    isSimulationReturnPreviousRow(asideRow, flow) &&
    isSimulationRowInAsideRunOfMain(state.rows, mainIndex, asidePointer, flow)
  ) {
    played = markSimulationPlayedReturnPrevious(played, simulationRowKey(asideRow, asidePointer));
  }
  const resumeRow = state.rows[returnPointer];
  return {
    overridePointer: null,
    returnTargetRowKey: null,
    playedReturnPreviousKeys: played,
    pointer: returnPointer,
    selectedItemIds: buildSimulationSelectedItemIds(
      state.rows,
      returnPointer,
      preserveSpeakerIds(state.selectedItemIds),
    ),
    ...syncSegmentFromRow(resumeRow),
    onAirStartedAtMs: touchSimulationOnAirClock(),
  };
}

function isActivelyPlayingSimulation(
  state: Pick<FlowControlSimulationCueState, 'activeSegmentKind' | 'selectedItemIds'>,
): boolean {
  return state.activeSegmentKind != null && state.selectedItemIds.length > 0;
}

function resolvePreservedSimulationPointer(
  previousRows: SimulationCueSheetRow[],
  previousPointer: number,
  rows: SimulationCueSheetRow[],
): number {
  const previousRow = previousRows[previousPointer];
  if (!previousRow) {
    return previousPointer;
  }
  const sameRowIndex = rows.findIndex(
    (row) => row.itemId === previousRow.itemId && row.scope === previousRow.scope && row.slotId === previousRow.slotId,
  );
  return sameRowIndex >= 0 ? sameRowIndex : previousPointer;
}

function preserveSimulationPlayoutState(
  state: FlowControlSimulationCueState,
  rows: SimulationCueSheetRow[],
  planFlowId: string,
  revision: string,
  simulationFlow?: FlowDto,
): Partial<FlowControlSimulationCueState> {
  const pointer = resolvePreservedSimulationPointer(state.rows, state.pointer, rows);
  const previousRow = state.rows[state.pointer];
  const currentRow = rows[pointer];
  const pointerRowChanged =
    previousRow?.itemId !== currentRow?.itemId ||
    previousRow?.scope !== currentRow?.scope ||
    previousRow?.slotId !== currentRow?.slotId;
  const speakerIds = preserveSpeakerIds(state.selectedItemIds);

  let overridePointer: number | null = null;
  if (state.overridePointer != null) {
    overridePointer = resolvePreservedSimulationPointer(state.rows, state.overridePointer, rows);
    if (overridePointer < 0 || overridePointer >= rows.length || !isSimulationRowPlayable(rows[overridePointer]!)) {
      overridePointer = null;
    }
  }

  const onAirPointer = overridePointer ?? pointer;
  const selectedItemIds =
    pointerRowChanged || overridePointer !== state.overridePointer
      ? buildSimulationSelectedItemIds(rows, onAirPointer, speakerIds)
      : state.selectedItemIds;

  return {
    planFlowId,
    revision,
    simulationFlow: simulationFlow ?? state.simulationFlow,
    rows,
    pointer,
    overridePointer,
    returnTargetRowKey: overridePointer != null ? state.returnTargetRowKey : null,
    playedReturnPreviousKeys: state.playedReturnPreviousKeys,
    selectedItemIds,
    activeSegmentKind: state.activeSegmentKind,
    activeSegmentAnchor: state.activeSegmentAnchor,
    activeSegmentSlotId: state.activeSegmentSlotId,
    onAirStartedAtMs: state.onAirStartedAtMs,
    onAirShowMode: state.onAirShowMode,
    nextShowMode: state.nextShowMode,
    slideIndexByItemId: state.slideIndexByItemId,
    totalPagesByItemId: state.totalPagesByItemId,
  };
}

/** Promote Next show mode onto On Air when the Next cue goes live. */
function promoteNextShowMode(
  state: Pick<FlowControlSimulationCueState, 'nextShowMode'>,
): Pick<FlowControlSimulationCueState, 'onAirShowMode'> {
  return { onAirShowMode: state.nextShowMode };
}

export const useFlowControlSimulationCueStore = create<FlowControlSimulationCueState>((set, get) => ({
  ...initialState,
  hydrateCueSheet: (planFlowId, rows, revision, simulationFlow) => {
    const state = get();
    const flowChanged = state.planFlowId !== planFlowId;
    const revisionChanged = flowChanged || state.revision !== revision;
    // Same-flow cue rebuilds (e.g. per-item nextShowMode patch) must not reset On Air / Next show mode.
    const softRefresh = !flowChanged && state.rows.length > 0;

    if ((revisionChanged || state.rows.length === 0) && !softRefresh) {
      if (isActivelyPlayingSimulation(state) && state.rows.length > 0) {
        set(preserveSimulationPlayoutState(state, rows, planFlowId, revision, simulationFlow));
        return;
      }
      const pointer = resolveInitialSimulationPointer(rows);
      set({
        planFlowId,
        revision,
        simulationFlow: simulationFlow ?? null,
        rows,
        pointer,
        overridePointer: null,
        returnTargetRowKey: null,
        playedReturnPreviousKeys: [],
        onAirShowMode: 'both',
        nextShowMode: 'both',
        heldNextShowModeCueIdentity: null,
        selectedItemIds: buildSimulationSelectedItemIds(rows, pointer),
        activeSegmentKind: null,
        activeSegmentAnchor: null,
        activeSegmentSlotId: undefined,
        slideIndexByItemId: {},
        totalPagesByItemId: {},
        onAirStartedAtMs: null,
      });
      return;
    }

    const isActivelyPlaying = isActivelyPlayingSimulation(state);
    if (isActivelyPlaying) {
      set(preserveSimulationPlayoutState(state, rows, planFlowId, revision, simulationFlow));
      return;
    }

    const previousRow = state.rows[state.pointer];
    let pointer = state.pointer;
    if (previousRow) {
      const sameRowIndex = rows.findIndex(
        (row) =>
          row.itemId === previousRow.itemId && row.scope === previousRow.scope && row.slotId === previousRow.slotId,
      );
      if (sameRowIndex >= 0 && isSimulationRowPlayable(rows[sameRowIndex]!)) {
        pointer = sameRowIndex;
      } else if (!isSimulationRowPlayable(rows[pointer]!)) {
        pointer = resolveInitialSimulationPointer(rows);
      }
    } else if (!isSimulationRowPlayable(rows[pointer]!)) {
      pointer = resolveInitialSimulationPointer(rows);
    }

    const selectedItemIds = state.selectedItemIds.filter((itemId) => {
      const row = rows.find((entry) => entry.itemId === itemId);
      return row != null && isSimulationRowPlayable(row);
    });

    set({
      planFlowId,
      revision,
      simulationFlow: simulationFlow ?? state.simulationFlow,
      rows,
      pointer,
      overridePointer: softRefresh ? state.overridePointer : null,
      returnTargetRowKey: softRefresh ? state.returnTargetRowKey : null,
      playedReturnPreviousKeys: softRefresh ? state.playedReturnPreviousKeys : [],
      selectedItemIds: buildSimulationSelectedItemIds(rows, pointer, selectedItemIds),
    });
  },
  reset: () => {
    useSimulationLocalCameraStore.getState().stop();
    set(initialState);
  },
  setOnAirShowMode: (showMode) => set({ onAirShowMode: showMode }),
  setNextShowMode: (showMode) => set({ nextShowMode: showMode }),
  holdNextShowModePreselect: (cueIdentity) => set({ heldNextShowModeCueIdentity: cueIdentity }),
  clearNextShowModePreselectHold: () => set({ heldNextShowModeCueIdentity: null }),
  stepMedia: (delta, flowFallback) => {
    const state = get();
    const flow = resolveSimulationFlowForActions(state, flowFallback);

    // Planned return_previous aside, or generic play-once insert.
    if (state.overridePointer != null) {
      const asideRow = state.rows[state.overridePointer];
      if (asideRow && isSimulationReturnPreviousRow(asideRow, flow) && state.returnTargetRowKey) {
        set({ ...leaveReturnPreviousAside(state, flow), ...promoteNextShowMode(state) });
        return;
      }
      // Next during insert is the resume row — end Play once and put resume on air.
      set({ ...resumeFromOverride(state), ...promoteNextShowMode(state) });
      return;
    }

    const result = resolveSimulationStepMedia(state.rows, state.pointer, state.selectedItemIds, delta, flow, {
      overridePointer: state.overridePointer,
      returnTargetRowKey: state.returnTargetRowKey,
      playedReturnPreviousKeys: state.playedReturnPreviousKeys,
    });
    switch (result.action) {
      case 'none':
        return;
      case 'scope_idle': {
        set({
          ...simulationScopeIdleSelection(state.rows, result.pointer),
          overridePointer: null,
          returnTargetRowKey: null,
        });
        return;
      }
      case 'replay': {
        set({
          overridePointer: null,
          returnTargetRowKey: null,
          ...syncPointerSelection(state, result.pointer),
          onAirStartedAtMs: touchSimulationOnAirClock(),
        });
        return;
      }
      case 'enter_aside': {
        const asideRow = state.rows[result.asidePointer]!;
        const returnRow = state.rows[result.returnTargetPointer]!;
        const speakerIds = preserveSpeakerIds(state.selectedItemIds);
        set({
          pointer: result.returnTargetPointer,
          overridePointer: result.asidePointer,
          returnTargetRowKey: simulationRowKey(returnRow, result.returnTargetPointer),
          selectedItemIds: buildSimulationSelectedItemIds(state.rows, result.asidePointer, speakerIds),
          ...syncSegmentFromRow(asideRow),
          ...promoteNextShowMode(state),
          onAirStartedAtMs: touchSimulationOnAirClock(),
        });
        return;
      }
      case 'leave_aside': {
        set({ ...leaveReturnPreviousAside(state, flow), ...promoteNextShowMode(state) });
        return;
      }
      case 'advance': {
        const segment = resolveSimulationSegmentFromRow(state.rows[result.pointer]);
        set({
          overridePointer: null,
          returnTargetRowKey: null,
          ...syncPointerSelection(state, result.pointer),
          ...promoteNextShowMode(state),
          activeSegmentKind: (segment?.segmentKind as FlowControlSegmentKind) ?? null,
          activeSegmentAnchor: segment?.anchor ?? null,
          activeSegmentSlotId: segment?.slotId,
          onAirStartedAtMs: touchSimulationOnAirClock(),
        });
        return;
      }
    }
  },
  playMedia: (itemId, playlistContext, cuePlayMode = 'jump') => {
    const state = get();
    const flow = resolveSimulationFlowForActions(state);
    const ordinal = findSimulationRowIndex(state.rows, itemId, playlistContext, state.pointer);
    if (ordinal < 0 || !isSimulationRowPlayable(state.rows[ordinal]!)) {
      return;
    }
    const row = state.rows[ordinal]!;
    const segment = resolveSimulationSegmentFromRow(row);
    const expandId = resolveFlowControlExpandIdFromSegment(segment?.segmentKind, segment?.slotId, row);
    if (expandId) {
      useFlowControlMediaExpandStore.getState().setExpandId(expandId, 'playing');
    }
    const speakerIds = preserveSpeakerIds(state.selectedItemIds);
    // Direct play: take this row's nextShowMode preselect onto On Air (inherit → current On Air).
    const onAirShowMode = resolveNextShowModeFromPreselect(row.nextShowMode, state.onAirShowMode);

    if (cuePlayMode === 'insert') {
      const mainPointer = resolveSimulationMainPointer(state.rows, state.pointer, flow) ?? state.pointer;
      const mainRow = state.rows[mainPointer];
      const isAside = isSimulationReturnPreviousRow(row, flow);
      let played = state.playedReturnPreviousKeys;
      let returnTargetRowKey: string | null = null;
      if (isAside && mainRow) {
        returnTargetRowKey = simulationRowKey(mainRow, mainPointer);
        if (isSimulationRowInAsideRunOfMain(state.rows, mainPointer, ordinal, flow)) {
          played = markSimulationPlayedReturnPrevious(played, simulationRowKey(row, ordinal));
        }
      }
      set({
        // Keep auto/resume pointer; only On air moves to the insert row.
        overridePointer: ordinal,
        returnTargetRowKey: isAside ? returnTargetRowKey : null,
        playedReturnPreviousKeys: played,
        selectedItemIds: buildSimulationSelectedItemIds(state.rows, ordinal, speakerIds),
        ...syncSegmentFromRow(row),
        onAirShowMode,
        onAirStartedAtMs: touchSimulationOnAirClock(),
      });
      return;
    }

    // Jumping the auto cue backward re-arms return_previous one-shots from the landing row onward.
    let played = state.playedReturnPreviousKeys;
    if (ordinal < state.pointer) {
      played =
        clearSimulationPlayedReturnPreviousFromIndex(state.rows, ordinal, state.playedReturnPreviousKeys, flow) ?? [];
    }
    set({
      overridePointer: null,
      returnTargetRowKey: null,
      playedReturnPreviousKeys: played,
      ...syncPointerSelection(state, ordinal),
      ...syncSegmentFromRow(row),
      onAirShowMode,
      onAirStartedAtMs: touchSimulationOnAirClock(),
    });
  },
  stopMedia: (itemId, playlistContext) => {
    const state = get();
    const flow = resolveSimulationFlowForActions(state);
    const onAirPointer = resolveSimulationOnAirPointer(state);
    const ordinal = findSimulationRowIndex(state.rows, itemId, playlistContext, onAirPointer);
    if (ordinal < 0 || ordinal !== onAirPointer) {
      return;
    }
    if (state.overridePointer != null) {
      const asideRow = state.rows[state.overridePointer];
      if (asideRow && isSimulationReturnPreviousRow(asideRow, flow) && state.returnTargetRowKey) {
        set({ ...leaveReturnPreviousAside(state, flow), ...promoteNextShowMode(state) });
        return;
      }
      set({ ...resumeFromOverride(state), ...promoteNextShowMode(state) });
      return;
    }
    const scopeIdleItemId = resolveScopeIdlePlayItemId(state.rows, state.pointer);
    if (!scopeIdleItemId) {
      set({
        selectedItemIds: [],
        onAirStartedAtMs: null,
      });
      return;
    }
    set({
      selectedItemIds: [scopeIdleItemId],
      onAirStartedAtMs: touchSimulationOnAirClock(),
    });
  },
  playSegment: (segmentKind, slotId) => {
    const state = get();
    const anchor = segmentKindToAnchor(segmentKind);
    const pointer = resolveSimulationSegmentPlayPointer(state.rows, anchor, slotId, segmentKind);
    if (pointer < 0) {
      return;
    }
    const expandId = resolveFlowControlExpandIdFromSegment(segmentKind, slotId);
    if (expandId) {
      useFlowControlMediaExpandStore.getState().setExpandId(expandId, 'playing');
    }
    let played = state.playedReturnPreviousKeys;
    if (pointer < state.pointer) {
      played =
        clearSimulationPlayedReturnPreviousFromIndex(
          state.rows,
          pointer,
          state.playedReturnPreviousKeys,
          resolveSimulationFlowForActions(state),
        ) ?? [];
    }
    set({
      overridePointer: null,
      returnTargetRowKey: null,
      playedReturnPreviousKeys: played,
      ...syncPointerSelection(state, pointer),
      activeSegmentKind: segmentKind,
      activeSegmentAnchor: anchor,
      activeSegmentSlotId: slotId,
      onAirStartedAtMs: touchSimulationOnAirClock(),
    });
  },
  stopSegment: (segmentKind, slotId) => {
    const state = get();
    if (state.activeSegmentKind !== segmentKind) {
      return;
    }
    if (segmentKind === 'session' || segmentKind === 'break' || isFlowControlInBetweenSegmentKind(segmentKind)) {
      if (slotId && state.activeSegmentSlotId !== slotId) {
        return;
      }
    }
    const nextPointer = resolveSimulationAdvancePastSegment(state.rows, state.pointer);
    if (nextPointer == null) {
      set({
        selectedItemIds: [],
        overridePointer: null,
        returnTargetRowKey: null,
        activeSegmentKind: null,
        activeSegmentAnchor: null,
        activeSegmentSlotId: undefined,
        onAirStartedAtMs: null,
      });
      return;
    }
    const nextRow = state.rows[nextPointer];
    set({
      overridePointer: null,
      returnTargetRowKey: null,
      ...syncPointerSelection(state, nextPointer),
      ...syncSegmentFromRow(nextRow),
      ...promoteNextShowMode(state),
      onAirStartedAtMs: touchSimulationOnAirClock(),
    });
  },
  stepSlide: (itemId, delta) => {
    const state = get();
    const current = state.slideIndexByItemId[itemId] ?? 1;
    const max = state.totalPagesByItemId[itemId];
    let next = Math.max(1, current + delta);
    if (max) {
      next = Math.min(next, max);
    }
    set({
      slideIndexByItemId: { ...state.slideIndexByItemId, [itemId]: next },
    });
  },
  setSlideIndex: (itemId, page) => {
    const state = get();
    const max = state.totalPagesByItemId[itemId];
    let next = Math.max(1, page);
    if (max) {
      next = Math.min(next, max);
    }
    set({
      slideIndexByItemId: { ...state.slideIndexByItemId, [itemId]: next },
    });
  },
  reportSlidePageCount: (itemId, totalPages) => {
    const state = get();
    if (state.totalPagesByItemId[itemId] === totalPages) {
      return;
    }
    const current = state.slideIndexByItemId[itemId] ?? 1;
    set({
      totalPagesByItemId: { ...state.totalPagesByItemId, [itemId]: totalPages },
      slideIndexByItemId: {
        ...state.slideIndexByItemId,
        [itemId]: Math.min(current, totalPages),
      },
    });
  },
  toggleItem: (itemId) => {
    const state = get();
    const isSelected = state.selectedItemIds.includes(itemId);
    if (isSelected) {
      set({ selectedItemIds: state.selectedItemIds.filter((id) => id !== itemId) });
      return;
    }
    set({ selectedItemIds: [...state.selectedItemIds, itemId] });
  },
  handlePlaybackComplete: (itemId) => {
    const state = get();
    if (shouldIgnoreSimulationPlaybackComplete(state.onAirStartedAtMs)) {
      return;
    }

    const flow = resolveSimulationFlowForActions(state);

    if (state.overridePointer != null) {
      const overrideRow = state.rows[state.overridePointer];
      if (overrideRow?.itemId === itemId) {
        if (isSimulationReturnPreviousRow(overrideRow, flow) && state.returnTargetRowKey) {
          set({ ...leaveReturnPreviousAside(state, flow), ...promoteNextShowMode(state) });
          return;
        }
        // Play once finished — return to resume/auto cue (Next target).
        set({ ...resumeFromOverride(state), ...promoteNextShowMode(state) });
        return;
      }
    }

    const onAirPointer = resolveSimulationOnAirPointer(state);
    const result = resolveSimulationPlaybackComplete(state.rows, onAirPointer, itemId, flow);
    switch (result.action) {
      case 'none':
        return;
      case 'return_previous':
        if (state.overridePointer != null && state.returnTargetRowKey) {
          set({ ...leaveReturnPreviousAside(state, flow), ...promoteNextShowMode(state) });
        }
        return;
      case 'scope_idle':
        set({
          ...simulationScopeIdleSelection(state.rows, state.pointer),
          overridePointer: null,
          returnTargetRowKey: null,
        });
        return;
      case 'stop':
        set({
          selectedItemIds: [],
          overridePointer: null,
          returnTargetRowKey: null,
          activeSegmentKind: null,
          activeSegmentAnchor: null,
          activeSegmentSlotId: undefined,
          onAirStartedAtMs: null,
        });
        return;
      case 'advance': {
        const nextRow = state.rows[result.pointer];
        set({
          overridePointer: null,
          returnTargetRowKey: null,
          ...syncPointerSelection(state, result.pointer),
          ...syncSegmentFromRow(nextRow),
          ...promoteNextShowMode(state),
          onAirStartedAtMs: touchSimulationOnAirClock(),
        });
        return;
      }
    }
  },
}));
